import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { FeatureOption, FilterKeys } from '@utils/pageConfig/Types';
import { MainFiltersActionType } from '@state/actions/Type';
import { getSessionStorage, setSessionStorage, StorageKey } from '@utils/StorageUtils';
import { NO_OPTIONS } from '@utils/StringConstants';
import { MainFiltersAction } from '@state/actions/main-filter/MainFiltersAction';
import { BaseTimePeriod } from '@services/FiltersTypes';

export type CustomMonthsPeriodState = {
  period: BaseTimePeriod | null;
  comparisonPeriod: BaseTimePeriod | null;
};

export interface MainFiltersState {
  timeFrame: Periodicity | null;
  period: BaseTimePeriod | null;
  comparisonPeriod: RelativePeriod | BaseTimePeriod | null;
  selectedGrowthType: GrowthTypes;
  feature: FeatureOption;
  segmentByFeature?: FeatureOption;
  customMonthsPeriodState: CustomMonthsPeriodState;
  customWeeksPeriodState: CustomMonthsPeriodState;
  selectedBrandBubble?: string;
}

export const defaultState: MainFiltersState = {
  period: null,
  timeFrame: null,
  comparisonPeriod: null,
  customMonthsPeriodState: {
    period: null,
    comparisonPeriod: null,
  },
  customWeeksPeriodState: {
    period: null,
    comparisonPeriod: null,
  },
  selectedGrowthType: GrowthTypes.percentage,
  feature: {
    label: NO_OPTIONS,
    value: NO_OPTIONS,
    dataType: NO_OPTIONS,
    feature: NO_OPTIONS,
    filterKey: FilterKeys.BRAND,
  },
  segmentByFeature: undefined,
  selectedBrandBubble: undefined,
};

const getInitialState = () => {
  const rawOpportunityReviewMainFilters = getSessionStorage(StorageKey.OpportunityReviewMainFilters);
  const filters = rawOpportunityReviewMainFilters && JSON.parse(rawOpportunityReviewMainFilters);
  return filters?.period &&
    filters?.timeFrame &&
    filters?.comparisonPeriod &&
    filters?.selectedGrowthType &&
    filters?.feature
    ? {
        period: filters.period,
        timeFrame: filters.timeFrame,
        comparisonPeriod: filters.comparisonPeriod,
        selectedGrowthType: filters.selectedGrowthType,
        feature: filters.feature,
        segmentByFeature: filters.segmentByFeature,
        customMonthsPeriodState: filters.customMonthsPeriodState,
        customWeeksPeriodState: filters.customWeeksPeriodState,
        selectedBrandBubble: filters.selectedBrandBubble,
      }
    : Object.freeze(defaultState);
};

export type UpdateMainFiltersPayload = {
  timeFrame?: Periodicity;
  period?: BaseTimePeriod;
  comparisonPeriod?: RelativePeriod | BaseTimePeriod;
  selectedGrowthType?: GrowthTypes;
  feature?: FeatureOption;
  segmentByFeature?: FeatureOption;
  customMonthsPeriodState?: CustomMonthsPeriodState;
  customWeeksPeriodState?: CustomMonthsPeriodState;
  selectedBrandBubble?: string;
};

const MainFiltersReducer = (
  state: MainFiltersState = getInitialState(),
  action?: MainFiltersAction,
): MainFiltersState => {
  if (action) {
    const actionType = action.type;
    switch (actionType) {
      case MainFiltersActionType.UpdateMainFilters: {
        const finalPayLoad = {
          ...state,
          ...action.payload,
        };
        setSessionStorage(StorageKey.OpportunityReviewMainFilters, JSON.stringify(finalPayLoad));
        return finalPayLoad;
      }
      case MainFiltersActionType.UpdateMFPeriod:
      case MainFiltersActionType.UpdateMFPeriodicity:
      case MainFiltersActionType.UpdateMFComparisonPeriod:
      case MainFiltersActionType.UpdateMFCustomMonthsPeriodState:
      case MainFiltersActionType.UpdateMFCustomWeeksPeriodState: {
        const field = findKey(actionType);
        const finalPayLoad = {
          ...state,
          [field]: action.payload,
        };
        setSessionStorage(StorageKey.OpportunityReviewMainFilters, JSON.stringify(finalPayLoad));
        return finalPayLoad;
      }
    }
  }
  return state;
};

const findKey = (actionType: MainFiltersActionType): keyof MainFiltersState => {
  switch (actionType) {
    case MainFiltersActionType.UpdateMFPeriod: {
      return 'period';
    }
    case MainFiltersActionType.UpdateMFPeriodicity: {
      return 'timeFrame';
    }
    case MainFiltersActionType.UpdateMFComparisonPeriod: {
      return 'comparisonPeriod';
    }
    case MainFiltersActionType.UpdateMFCustomMonthsPeriodState: {
      return 'customMonthsPeriodState';
    }
    case MainFiltersActionType.UpdateMFCustomWeeksPeriodState: {
      return 'customWeeksPeriodState';
    }
    default:
      throw new Error(`Find key: unsupported action type: ${actionType}`);
  }
};

export default MainFiltersReducer;
